import Image from 'next/image';
import useTranslation from 'shared/hooks/useTranslation';
import styled from 'styled-components';
import { formatAverageGrade, formatReviewsCount } from 'shared/utils/reviews';
import StarsGrade from 'shared/components/StarsGrade';
import NextLink from 'shared/components/NextLink';
import Button from 'shared/components/Button';
import ROUTES from 'config/routes';
import { VipConfig } from './vipConfigs';

type VipCardProps = VipConfig & {
  averageGrade: number;
  reviewsCount: number;
};

type CardProps = {
  withLink: boolean;
};

const Card = styled.div<CardProps>`
  box-shadow:
    0px 1.58632px 3.17264px rgba(7, 4, 146, 0.1),
    0px 19.0359px 47.5897px rgba(6, 47, 125, 0.05),
    0px 9.51793px 19.0359px rgba(27, 59, 119, 0.05);
  border-radius: 1.7rem;
  color: white;
  height: 45rem;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  width: 26.3rem;

  ${({ theme }) => theme.MEDIA.SM} {
    height: 60rem;
    width: 35rem;
  }
`;

const StyledImage = styled(Image)`
  z-index: -1;
`;

const ContentWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  left: 0;
  padding: 4rem 3.4rem;
  position: absolute;
  text-align: center;
  top: 0;
`;

const NameAndText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Name = styled.span`
  font-size: 2rem;
  line-height: 3rem;

  ${({ theme }) => theme.MEDIA.SM} {
    font-size: 2.7rem;
    line-height: 4.1rem;
  }
`;

const Text = styled.p`
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 1.5rem;

  ${({ theme }) => theme.MEDIA.SM} {
    font-size: 1.6rem;
    line-height: 2.1rem;
  }
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const Grade = styled.div`
  line-height: 100%;
  font-size: 4.2rem;
  font-weight: 600;

  ${({ theme }) => theme.MEDIA.SM} {
    font-size: 5.8rem;
  }
`;

const GradeText = styled.div`
  align-items: center;
  color: #ffffff;
  display: flex;
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.4rem;

  ${({ theme }) => theme.MEDIA.SM} {
    line-height: 1.9rem;
    font-size: 1.4rem;
  }
`;

const StyledButton = styled(Button)`
  background-color: black;
  color: white;
  margin-top: 1rem;
  width: 100%;

  ${({ theme }) => theme.MEDIA.SM} {
    margin-top: 2rem;
  }
`;

function VipCard({ description, background, name, averageGrade, reviewsCount, link }: VipCardProps): JSX.Element {
  const { t } = useTranslation('referencesPage');

  return (
    <Card withLink={!!link}>
      <StyledImage src={background.src} alt={background.alt} layout="fill" objectFit="cover" lazyBoundary="1000px" />
      <ContentWrapper>
        <NameAndText>
          <Name>{name}</Name>
          <Text>{t(`${description}`)}</Text>
        </NameAndText>
        <Bottom>
          <Grade>{formatAverageGrade(averageGrade)}</Grade>
          <StarsGrade grade={Math.ceil(averageGrade)} width={28} height={28} marginBottom="0" />
          <GradeText>
            {formatReviewsCount(reviewsCount)} {t('vip.customerReviews', 'common')}
          </GradeText>
          <NextLink href={`${ROUTES.REFERENCES}/${link}`} passHref shallow={!link}>
            <StyledButton>{t('vip.integration', 'common')}</StyledButton>
          </NextLink>
        </Bottom>
      </ContentWrapper>
    </Card>
  );
}

export default VipCard;
